/**
 * What a string might be, and where it could be opened.
 *
 * An id in this admin does not say what it belongs to. The same 36 characters
 * could be a purchase, a signup, a transaction or a locker, and the only way to
 * find out today is to try the pages one at a time. This file is the list of
 * places worth trying, so a paste can offer them all at once.
 *
 * Only routes that take a bare id are here. `/next/participant/:id` qualifies;
 * `/next/reports/sales-new` does not, and neither does anything needing two
 * parameters — offering a link that cannot be built is worse than not offering
 * it.
 */

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = value => GUID.test(String(value || '').trim());

// Deliberately loose. It decides whether to search without waiting out the
// debounce, not whether to accept anything.
export const looksLikeEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

/**
 * Ordered by how often an id somebody pastes turns out to be one of these.
 * Signups and purchases are what support and operations deal in; the system
 * records at the bottom are what a log line points at.
 *
 * An entry carries `to` for a page in this tree and `href` for one that is still
 * the old admin — the same distinction `nav.js` makes, and for the same reason:
 * an old-admin path has to be a full page load, or it renders inside a shell
 * that was never meant to hold it.
 */
export const ID_ROUTES = [
  { label: 'Signup', to: id => `/next/signup/${id}` },
  { label: 'Purchase', to: id => `/next/purchase/${id}` },
  { label: 'User', to: id => `/next/user/${id}` },
  { label: 'Facility', to: id => `/next/facility/${id}` },
  { label: 'Provider', to: id => `/next/provider/${id}` },
  // The product detail is not rebuilt — 5.000 lines of pricing and schedules in
  // the old admin — so this one leaves the tree. Product ids are common enough
  // in a paste that leaving them unanswerable would be the obvious gap.
  { label: 'Product', href: id => `/product/${id}` },
  { label: 'Participant', to: id => `/next/participant/${id}` },
  { label: 'Transaction', to: id => `/next/transaction/${id}` },
  { label: 'Subscription', to: id => `/next/subscription/${id}` },
  { label: 'Confirmation email', to: id => `/next/email/${id}` },
  { label: 'Seasonal court', to: id => `/next/seasonal-court/${id}` },
  { label: 'Seasonal court slot', to: id => `/next/seasonal-court-slot/${id}` },
  { label: 'Seasonal court purchase', to: id => `/next/seasonal-court-purchase/${id}` },
  { label: 'Activity', to: id => `/next/activity/${id}` },
  { label: 'Area', to: id => `/next/area/${id}` },
  { label: 'Locker', to: id => `/next/locker/${id}` },
  { label: 'Crawling', to: id => `/next/crawling/${id}` },
  { label: 'Relay rule', to: id => `/next/relay-rule/${id}` },
  { label: 'Resource group', to: id => `/next/resource-group/${id}` },
  { label: 'External purchase', to: id => `/next/external-purchase/${id}` },
  { label: 'External purchase attempt', to: id => `/next/external-purchase-attempt/${id}` },
  { label: 'External activity', to: id => `/next/external-activity/${id}` },
  { label: 'Booking agreement', to: id => `/next/booking-agreement/${id}` },
  { label: 'Non-partner facility', to: id => `/next/non-partner-facility/${id}` },
  { label: 'Facility redirect', to: id => `/next/facility-redirect/${id}` },
  { label: 'Provider admin', to: id => `/next/provider-admin/${id}` },
  { label: 'SEO text', to: id => `/next/seo-text/${id}` },
  { label: 'Play event', to: id => `/next/play-event/${id}` }
];
